import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { useInViewport, useSafeState } from 'ahooks';
import isInViewPort from 'utils/isInViewPort';
import { LoadErrorFooter, LoadingMoreFooter, NoMoreFooter } from './Footer';

type LoadRenderer = (
  nodes: {
    loadMore: ReactNode;
    noMore: ReactNode;
    loadError: ReactNode;
  },
  hasMore: boolean,
  hasError: boolean,
) => ReactNode;

const defaultLoadRenderer: LoadRenderer = (nodes, hasMore, hasError) => {
  const { loadMore, noMore, loadError } = nodes;

  if (hasError) {
    return loadError;
  }

  if (hasMore) {
    return loadMore;
  }

  return noMore;
};

export interface LoadMoreProps {
  children: ReactNode | ((loadEle: ReactNode) => ReactNode);
  hasMore: boolean;
  // recoil 状态和 react 状态更新时机不一致，当 hasMore 参数为 recoil 状态时，需传入此 loading recoil 状态参数。
  loading?: boolean;
  onLoadMore: () => Promise<any>;
  loadRenderer?: LoadRenderer;
  loadingDelay?: number;
}

function LoadMore(props: LoadMoreProps) {
  const {
    children,
    hasMore,
    loading: loadingProp,
    onLoadMore,
    loadRenderer = defaultLoadRenderer,
    loadingDelay = 0,
  } = props;

  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [loadMoreVisible] = useInViewport(loadMoreRef);

  const [loading, setLoading] = useSafeState(loadingProp);

  const [hasError, setHasError] = useSafeState(false);

  const loadEle = useMemo(() => {
    const loadMore = <LoadingMoreFooter />;
    const noMore = <NoMoreFooter />;
    const loadError = <LoadErrorFooter onClick={() => setHasError(false)} />;

    const ele = loadRenderer(
      {
        loadMore,
        noMore,
        loadError,
      },
      hasMore,
      hasError,
    );

    return <div ref={loadMoreRef}>{ele}</div>;
  }, [hasError, hasMore, loadRenderer, setHasError]);

  const refs = useRef({
    onLoadMore,
    loadingProp,
  });
  refs.current = {
    onLoadMore,
    loadingProp,
  };

  useEffect(() => {
    let needLoad = true;

    if (!hasMore || hasError || loading || !loadMoreVisible || !isInViewPort(loadMoreRef.current)) {
      needLoad = false;
    }

    if (needLoad) {
      const {
        current: { onLoadMore, loadingProp },
      } = refs;

      if (loadingProp === undefined) {
        setLoading(true);
      }

      onLoadMore()
        .catch(() => {
          setHasError(true);
        })
        .finally(() => {
          if (loadingProp !== undefined) {
            return;
          }

          if (loadingDelay > 0) {
            setTimeout(() => setLoading(false), loadingDelay);
          } else {
            setLoading(false);
          }
        });
    }
  }, [loadMoreVisible, loading, hasMore, hasError, loadingDelay, setLoading, setHasError]);

  useEffect(() => {
    if (loadingProp !== undefined) {
      setLoading(loadingProp);
    }
  }, [loadingProp, setLoading]);

  if (typeof children === 'function') {
    return children(loadEle);
  }

  return (
    <>
      {children}
      {loadEle}
    </>
  );
}

export default LoadMore;
